import { Theme } from '@fluentui/react';

export type TimelineItem = {
    name: string;
    description: string;
    pendingName?: string;
    pendingDescription?: string;
    cloud: string;
    status: string;
    statusDetails: string;
    timestamp: string;
    isProjected?: boolean;
    isValid?: boolean;
    shouldDisplay: boolean;
};

export type TimelineStepText = {
    name: string;
    description: string;
};

export type TimelineDependency = {
    uniqueIdentifier: string;
    displayName: string;
    dependencyType: 'Build' | 'Deployment';
    completionIndicator: string;
    completionIndicatorString: string;
    isAvailable: boolean;
};

export const getDependencyDisplayName = (
    dependencyType: 'Build' | 'Deployment',
    uniqueIdentifier: string,
    deploymentDisplayName?: string,
    stageName?: string
): string => {
    if (dependencyType === 'Build') {
        return uniqueIdentifier;
    }

    const displayName = deploymentDisplayName || uniqueIdentifier;
    return stageName ? `${stageName} > ${displayName}` : displayName;
};

/**
 * Returns the text to display for a timeline step. Descriptions are shown only
 * while the step is in progress or after it reaches a terminal status.
 */
export const getTimelineStepText = (
    name: string,
    description: string,
    isInProgress: boolean,
    isTerminal: boolean,
    pendingName?: string,
    pendingDescription?: string
): TimelineStepText => {
    if (isTerminal) {
        return { name, description };
    }

    return {
        name: pendingName || name,
        description: isInProgress ? pendingDescription || '' : '',
    };
};

export type CompletionStatusInfo = {
    name: string;
    iconName: string;
    iconColor?: string;
    tooltip?: string;
};

export const CompletionStatus: Record<string, CompletionStatusInfo> = {
    NotStarted: { name: 'Not Started', iconName: 'CircleRing' },
    Failed: { name: 'Failed', iconName: 'StatusErrorFull' },
    Cancelled: { name: 'Cancelled', iconName: 'BlockedSiteSolid12' },
    Processing: { name: 'Processing', iconName: 'Clock' },
    Inherited: {
        name: 'Completed',
        iconName: 'Completed',
        tooltip:
            'This status has been inherited from another replication of the same build or deployment, for the same cloud.',
    },
    Transferred: {
        name: 'Completed',
        iconName: 'Completed',
        tooltip:
            'This is the last status for a replication for a non-egressable cloud.',
    },
    Completed: { name: 'Completed', iconName: 'CompletedSolid' },
};

export type TimelineProgress = {
    /**
     * Index of the step to present as "In Progress", or -1 when nothing is running - either
     * every step has started, or the run ended early on a terminal status.
     */
    inProgressIndex: number;

    /**
     * Index of the terminal step that ended the run before the remaining steps could start,
     * or -1 when the run did not end early.
     */
    endedAtIndex: number;
};

/**
 * Statuses that end a run. Nothing after one of these is going to start, so no later step may be
 * presented as running.
 */
export const isTerminalStatus = (status: string | undefined): boolean =>
    status === 'Failed' || status === 'Cancelled';

/**
 * A step whose own status is final, so it is labelled with its completed name rather than the
 * in-progress wording. Every surface that labels a step must agree on this.
 * Inherited and Transferred are completed-equivalent - CompletionStatus renders both as Completed.
 */
export const isFinalStepStatus = (status: string | undefined): boolean =>
    status === 'Completed' ||
    status === 'Inherited' ||
    status === 'Transferred' ||
    isTerminalStatus(status);

/**
 * Works out how far a timeline actually got. Both the step list and the duration chart render from
 * this, so they cannot disagree about whether something is still in progress.
 *
 * Pass the already-filtered (shouldDisplay) items - the indexes returned are positions in that
 * same array.
 */
export const getTimelineProgress = (
    visibleTimeline: TimelineItem[]
): TimelineProgress => {
    const firstNotStartedIndex = visibleTimeline.findIndex(
        (item) => item.status === 'NotStarted'
    );

    // Index 0 has no preceding step that could have ended the run.
    const endedAtIndex =
        firstNotStartedIndex > 0 &&
        isTerminalStatus(visibleTimeline[firstNotStartedIndex - 1].status)
            ? firstNotStartedIndex - 1
            : -1;

    if (endedAtIndex !== -1) {
        return { inProgressIndex: -1, endedAtIndex };
    }

    // A step reported as Processing is running right now, so it takes the marker. Without this the
    // positional guess below lands on the step *after* it - the one waiting on it to finish.
    const processingIndex = visibleTimeline.findIndex(
        (item) => item.status === 'Processing'
    );

    return {
        inProgressIndex:
            processingIndex !== -1 ? processingIndex : firstNotStartedIndex,
        endedAtIndex: -1,
    };
};

export const getStatusIconProperties = (
    status: string | undefined,
    theme: Theme
): CompletionStatusInfo => {
    let completionStatusInfo = CompletionStatus['NotStarted'];
    if (status && CompletionStatus[status]) {
        completionStatusInfo = CompletionStatus[status];
        switch (status) {
            case 'NotStarted':
                return {
                    ...completionStatusInfo,
                    iconColor: '',
                };
            case 'Failed':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.semanticColors.errorIcon,
                };
            case 'Cancelled':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.semanticColors.warningHighlight,
                };
            case 'Processing':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.palette.themePrimary,
                };
            case 'Inherited':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.semanticColors.successIcon,
                };
            case 'Transferred':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.semanticColors.successIcon,
                };
            case 'Completed':
                return {
                    ...completionStatusInfo,
                    iconColor: theme.semanticColors.successIcon,
                };
        }
    }
    return completionStatusInfo;
};

export type CompletionStatusTracker = {
    currentStep: number;
    totalSteps: number;
};
